import { ThemeService } from '@/app/core/services/theme/theme.service';
import {Component, Input, computed, inject} from '@angular/core';
import {Router} from '@angular/router';

@Component({
  selector: 'app-guia-component',
  standalone: true,
  imports: [],
  templateUrl: './guia-component.html',
  styleUrl: './guia-component.scss'
})
export class GuiaComponent {
  private themeService = inject(ThemeService);
  private router = inject(Router);

  protected themeContent = this.themeService.themeContent;
  protected guideComponent = computed(() => this.themeContent()?.guideComponent);

  @Input() GUIA_KEY: string = "";
  @Input() data: any[] = [];

  evaluations: any = {};

  private categoryTotals: Record<string, number> = {};
  private domainTotals: Record<string, number> = {};
  private dimensionTotals: Record<string, number> = {};

  categoryTotalsArray: { key: string, value: number }[] = [];
  domainTotalsArray: { key: string, value: number }[] = [];
  dimensionTotalsArray: { key: string, value: number }[] = [];

  domainEvaluations: Record<string, string> = {};
  categoryEvaluations: Record<string, string> = {};
  finalEvaluation: string = '';

  totalSum: number = 0;

  private domainRulesMap: Record<string, any[]> = {};
  private categoryRulesMap: Record<string, any[]> = {};
  private finalEvaluationRules: any[] = [];

  ngOnInit() {
    if (this.GUIA_KEY === 'GUIA II') {
      this.evaluations = this.evaluationsGuiaDos;
    } else if (this.GUIA_KEY === 'GUIA III') {
      this.evaluations = this.evaluationsGuiaTres;
    } else {
      // Default to guia3 or handle error
      this.evaluations = this.evaluationsGuiaTres;
    }
    this.precomputeEvaluationRules();
    this.processResults(this.data);
    this.calculateAllEvaluations();
  }

  private precomputeEvaluationRules() {
    this.evaluations.domains.forEach((domain: any) => {
      this.domainRulesMap[domain.category] = domain.rules;
    });

    this.evaluations.categories.forEach((category: any) => {
      this.categoryRulesMap[category.category] = category.rules;
    });

    this.finalEvaluationRules = this.evaluations.finalWeighting.rules;
  }

  private calculateAllEvaluations() {
    this.calculateEvaluations(this.domainTotalsArray, this.domainRulesMap, this.domainEvaluations);
    this.calculateEvaluations(this.categoryTotalsArray, this.categoryRulesMap, this.categoryEvaluations);
    this.calculateFinalEvaluation();
  }

  private calculateEvaluations(
    items: { key: string, value: number }[],
    rulesMap: Record<string, any[]>,
    resultStore: Record<string, string>
  ) {
    for (const item of items) {
      const rules = rulesMap[item.key];
      if (!rules) continue;

      const evaluation = rules.find(rule =>
        item.value >= rule.lowerLimit &&
        (rule.upperLimit === null || item.value <= rule.upperLimit)
      );

      if (evaluation) {
        resultStore[item.key] = evaluation.rating;
      }
    }
  }

  private calculateFinalEvaluation() {
    if (this.GUIA_KEY === 'GUIA I') {
      // Para guia-1, si la suma es mayor a 0, mostrar "Muy alto"
      if (this.totalSum > 0) {
        this.finalEvaluation = 'Muy alto';
      } else {
        this.finalEvaluation = 'Nulo o despreciable';
      }
      return;
    }

    // Para otras guías, usar las reglas normales
    const evaluation = this.finalEvaluationRules.find(rule =>
      this.totalSum >= rule.lowerLimit &&
      (rule.upperLimit === null || this.totalSum <= rule.upperLimit)
    );
    this.finalEvaluation = evaluation?.rating || '';
  }

  processResults(results: any[]) {
    this.totalSum = 0;
    this.categoryTotals = {};
    this.domainTotals = {};
    this.dimensionTotals = {};

    for (const row of results) {
      const { category, domain, dimension, score } = row;
      this.totalSum += score;

      this.categoryTotals[category] = (this.categoryTotals[category] || 0) + score;
      this.domainTotals[domain] = (this.domainTotals[domain] || 0) + score;
      this.dimensionTotals[dimension] = (this.dimensionTotals[dimension] || 0) + score;
    }

    this.categoryTotalsArray = this.mapToArray(this.categoryTotals);
    this.domainTotalsArray = this.mapToArray(this.domainTotals);
    this.dimensionTotalsArray = this.mapToArray(this.dimensionTotals);
  }

  private mapToArray(obj: Record<string, number>): { key: string, value: number }[] {
    return Object.keys(obj).map(key => ({ key, value: obj[key] }));
  }

  handleGoBack(){
    this.router.navigate(['resultados']);
  }


evaluationsGuiaDos = {
    "finalWeighting": {
      "category": "Calificación final del cuestionario",
      "rules": [
        {
          "rating": "Nulo o despreciable",
          "lowerLimit": 0,
          "upperLimit": 19
        },
        {
          "rating": "Bajo",
          "lowerLimit": 20,
          "upperLimit": 44
        },
        {
          "rating": "Medio",
          "lowerLimit": 45,
          "upperLimit": 69
        },
        {
          "rating": "Alto",
          "lowerLimit": 70,
          "upperLimit": 89
        },
        {
          "rating": "Muy alto",
          "lowerLimit": 90,
          "upperLimit": null
        }
      ]
    },
    "categories": [
      {
        "category": "Ambiente de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 2
          },
          {
            "rating": "Bajo",
            "lowerLimit": 3,
            "upperLimit": 4
          },
          {
            "rating": "Medio",
            "lowerLimit": 5,
            "upperLimit": 6
          },
          {
            "rating": "Alto",
            "lowerLimit": 7,
            "upperLimit": 8
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 9,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Factores propios de la actividad",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 9
          },
          {
            "rating": "Bajo",
            "lowerLimit": 10,
            "upperLimit": 19
          },
          {
            "rating": "Medio",
            "lowerLimit": 20,
            "upperLimit": 29
          },
          {
            "rating": "Alto",
            "lowerLimit": 30,
            "upperLimit": 39
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 40,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Organización del tiempo de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 3
          },
          {
            "rating": "Bajo",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Medio",
            "lowerLimit": 6,
            "upperLimit": 8
          },
          {
            "rating": "Alto",
            "lowerLimit": 9,
            "upperLimit": 11
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 12,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Liderazgo y relaciones en el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 9
          },
          {
            "rating": "Bajo",
            "lowerLimit": 10,
            "upperLimit": 17
          },
          {
            "rating": "Medio",
            "lowerLimit": 18,
            "upperLimit": 27
          },
          {
            "rating": "Alto",
            "lowerLimit": 28,
            "upperLimit": 37
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 38,
            "upperLimit": null
          }
        ]
      }
    ],
    "domains": [
      {
        "category": "Condiciones en el ambiente de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 2
          },
          {
            "rating": "Bajo",
            "lowerLimit": 3,
            "upperLimit": 4
          },
          {
            "rating": "Medio",
            "lowerLimit": 5,
            "upperLimit": 6
          },
          {
            "rating": "Alto",
            "lowerLimit": 7,
            "upperLimit": 8
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 9,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Carga de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 11
          },
          {
            "rating": "Bajo",
            "lowerLimit": 12,
            "upperLimit": 15
          },
          {
            "rating": "Medio",
            "lowerLimit": 16,
            "upperLimit": 19
          },
          {
            "rating": "Alto",
            "lowerLimit": 20,
            "upperLimit": 23
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 24,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Falta de control sobre el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 4
          },
          {
            "rating": "Bajo",
            "lowerLimit": 5,
            "upperLimit": 7
          },
          {
            "rating": "Medio",
            "lowerLimit": 8,
            "upperLimit": 10
          },
          {
            "rating": "Alto",
            "lowerLimit": 11,
            "upperLimit": 13
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 14,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Jornada de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 0
          },
          {
            "rating": "Bajo",
            "lowerLimit": 1,
            "upperLimit": 1
          },
          {
            "rating": "Medio",
            "lowerLimit": 2,
            "upperLimit": 3
          },
          {
            "rating": "Alto",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 6,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Interferencia en la relación trabajo-familia",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 0
          },
          {
            "rating": "Bajo",
            "lowerLimit": 1,
            "upperLimit": 1
          },
          {
            "rating": "Medio",
            "lowerLimit": 2,
            "upperLimit": 3
          },
          {
            "rating": "Alto",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 6,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Liderazgo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 2
          },
          {
            "rating": "Bajo",
            "lowerLimit": 3,
            "upperLimit": 4
          },
          {
            "rating": "Medio",
            "lowerLimit": 5,
            "upperLimit": 7
          },
          {
            "rating": "Alto",
            "lowerLimit": 8,
            "upperLimit": 10
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 11,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Relaciones en el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 4
          },
          {
            "rating": "Bajo",
            "lowerLimit": 5,
            "upperLimit": 7
          },
          {
            "rating": "Medio",
            "lowerLimit": 8,
            "upperLimit": 10
          },
          {
            "rating": "Alto",
            "lowerLimit": 11,
            "upperLimit": 13
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 14,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Violencia",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 6
          },
          {
            "rating": "Bajo",
            "lowerLimit": 7,
            "upperLimit": 9
          },
          {
            "rating": "Medio",
            "lowerLimit": 10,
            "upperLimit": 12
          },
          {
            "rating": "Alto",
            "lowerLimit": 13,
            "upperLimit": 15
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 16,
            "upperLimit": null
          }
        ]
      }
    ]
  }

  evaluationsGuiaTres = {
    "finalWeighting": {
      "category": "Calificación final del cuestionario",
      "rules": [
        {
          "rating": "Nulo o despreciable",
          "lowerLimit": 0,
          "upperLimit": 49
        },
        {
          "rating": "Bajo",
          "lowerLimit": 50,
          "upperLimit": 74
        },
        {
          "rating": "Medio",
          "lowerLimit": 75,
          "upperLimit": 98
        },
        {
          "rating": "Alto",
          "lowerLimit": 99,
          "upperLimit": 139
        },
        {
          "rating": "Muy alto",
          "lowerLimit": 140,
          "upperLimit": null
        }
      ]
    },
    "categories": [
      {
        "category": "Ambiente de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 4
          },
          {
            "rating": "Bajo",
            "lowerLimit": 5,
            "upperLimit": 8
          },
          {
            "rating": "Medio",
            "lowerLimit": 9,
            "upperLimit": 10
          },
          {
            "rating": "Alto",
            "lowerLimit": 11,
            "upperLimit": 13
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 14,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Factores propios de la actividad",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 14
          },
          {
            "rating": "Bajo",
            "lowerLimit": 15,
            "upperLimit": 29
          },
          {
            "rating": "Medio",
            "lowerLimit": 30,
            "upperLimit": 44
          },
          {
            "rating": "Alto",
            "lowerLimit": 45,
            "upperLimit": 59
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 60,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Organización del tiempo de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 4
          },
          {
            "rating": "Bajo",
            "lowerLimit": 5,
            "upperLimit": 6
          },
          {
            "rating": "Medio",
            "lowerLimit": 7,
            "upperLimit": 9
          },
          {
            "rating": "Alto",
            "lowerLimit": 10,
            "upperLimit": 12
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 13,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Liderazgo y relaciones en el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 13
          },
          {
            "rating": "Bajo",
            "lowerLimit": 14,
            "upperLimit": 28
          },
          {
            "rating": "Medio",
            "lowerLimit": 29,
            "upperLimit": 41
          },
          {
            "rating": "Alto",
            "lowerLimit": 42,
            "upperLimit": 57
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 58,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Entorno organizacional",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 9
          },
          {
            "rating": "Bajo",
            "lowerLimit": 10,
            "upperLimit": 13
          },
          {
            "rating": "Medio",
            "lowerLimit": 14,
            "upperLimit": 17
          },
          {
            "rating": "Alto",
            "lowerLimit": 18,
            "upperLimit": 22
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 23,
            "upperLimit": null
          }
        ]
      }
    ],
    "domains": [
      {
        "category": "Condiciones en el ambiente de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 4
          },
          {
            "rating": "Bajo",
            "lowerLimit": 5,
            "upperLimit": 8
          },
          {
            "rating": "Medio",
            "lowerLimit": 9,
            "upperLimit": 10
          },
          {
            "rating": "Alto",
            "lowerLimit": 11,
            "upperLimit": 13
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 14,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Reconocimiento del desempeño",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 5
          },
          {
            "rating": "Bajo",
            "lowerLimit": 6,
            "upperLimit": 9
          },
          {
            "rating": "Medio",
            "lowerLimit": 10,
            "upperLimit": 13
          },
          {
            "rating": "Alto",
            "lowerLimit": 14,
            "upperLimit": 17
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 18,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Carga de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 14
          },
          {
            "rating": "Bajo",
            "lowerLimit": 15,
            "upperLimit": 20
          },
          {
            "rating": "Medio",
            "lowerLimit": 21,
            "upperLimit": 26
          },
          {
            "rating": "Alto",
            "lowerLimit": 27,
            "upperLimit": 36
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 37,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Insuficiente sentido de pertenencia e inestabilidad",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 3
          },
          {
            "rating": "Bajo",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Medio",
            "lowerLimit": 6,
            "upperLimit": 7
          },
          {
            "rating": "Alto",
            "lowerLimit": 8,
            "upperLimit": 9
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 10,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Falta de control sobre el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 10
          },
          {
            "rating": "Bajo",
            "lowerLimit": 11,
            "upperLimit": 15
          },
          {
            "rating": "Medio",
            "lowerLimit": 16,
            "upperLimit": 20
          },
          {
            "rating": "Alto",
            "lowerLimit": 21,
            "upperLimit": 24
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 25,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Jornada de trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 0
          },
          {
            "rating": "Bajo",
            "lowerLimit": 1,
            "upperLimit": 1
          },
          {
            "rating": "Medio",
            "lowerLimit": 2,
            "upperLimit": 3
          },
          {
            "rating": "Alto",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 6,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Interferencia en la relación trabajo-familia",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 3
          },
          {
            "rating": "Bajo",
            "lowerLimit": 4,
            "upperLimit": 5
          },
          {
            "rating": "Medio",
            "lowerLimit": 6,
            "upperLimit": 7
          },
          {
            "rating": "Alto",
            "lowerLimit": 8,
            "upperLimit": 9
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 10,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Liderazgo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 8
          },
          {
            "rating": "Bajo",
            "lowerLimit": 9,
            "upperLimit": 11
          },
          {
            "rating": "Medio",
            "lowerLimit": 12,
            "upperLimit": 15
          },
          {
            "rating": "Alto",
            "lowerLimit": 16,
            "upperLimit": 19
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 20,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Relaciones en el trabajo",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 9
          },
          {
            "rating": "Bajo",
            "lowerLimit": 10,
            "upperLimit": 12
          },
          {
            "rating": "Medio",
            "lowerLimit": 13,
            "upperLimit": 16
          },
          {
            "rating": "Alto",
            "lowerLimit": 17,
            "upperLimit": 20
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 21,
            "upperLimit": null
          }
        ]
      },
      {
        "category": "Violencia",
        "rules": [
          {
            "rating": "Nulo o despreciable",
            "lowerLimit": 0,
            "upperLimit": 6
          },
          {
            "rating": "Bajo",
            "lowerLimit": 7,
            "upperLimit": 9
          },
          {
            "rating": "Medio",
            "lowerLimit": 10,
            "upperLimit": 12
          },
          {
            "rating": "Alto",
            "lowerLimit": 13,
            "upperLimit": 15
          },
          {
            "rating": "Muy alto",
            "lowerLimit": 16,
            "upperLimit": null
          }
        ]
      }
    ]
  }
}
